import { getCompany } from '../api/company/company';
import { FALLBACK_DEMO_SERVICES } from './fixtures/sampleServices';
import { formatStaffDisplayName } from '../calendar_v3/utils/staffDisplayName';
import { normalizeWorkingSchedule } from '../shared/booking/employee-schedule/workingSchedule';
import {
  fetchCompanyLocations,
  getDefaultLocation,
} from '../locations/locationRepository';
import { resolveLocationHours } from '../locations/resolveLocationHours';
import { listServices } from '../api/companyRecords';
import { fetchCompanyStaff } from '../shared/booking/service-staff/serviceStaffRouting';

export async function loadCompanySeedContext({
  companyId,
  ownerId,
  useCompanyData = true,
}) {
  if (!companyId || !ownerId) {
    throw new Error('Company and owner are required to load demo data context.');
  }

  let companyHours = {};
  let companySnapshotData = null;

  if (useCompanyData) {
    try {
      companySnapshotData = await getCompany({ companyId });
      companyHours = companySnapshotData?.settings?.companyHours || {};
    } catch (err) {
      console.warn('[loadCompanySeedContext] Could not load company hours:', err);
    }

    try {
      const locations = await fetchCompanyLocations(companyId);
      const defaultLocation = getDefaultLocation(locations);
      if (defaultLocation) {
        companyHours = resolveLocationHours(defaultLocation, {
          ...companySnapshotData,
          settings: {
            ...(companySnapshotData?.settings || {}),
            companyHours,
          },
        });
      }
    } catch (err) {
      console.warn('[loadCompanySeedContext] Could not resolve location hours:', err);
    }
  }

  const staffMembers = await fetchCompanyStaff({ companyId, ownerId });
  const staff = staffMembers.map((member) => ({
    ...member,
    displayName: formatStaffDisplayName(member),
    workingSchedule: normalizeWorkingSchedule(member.workingSchedule, companyHours),
  }));

  if (staff.length === 0) {
    throw new Error('Add at least one team member before seeding demo appointments.');
  }

  if (!useCompanyData) {
    return {
      staff,
      services: FALLBACK_DEMO_SERVICES,
      usingCompanyData: false,
      companyHours: {},
    };
  }

  let services = [];
  try {
    const servicesSnap = await listServices({ companyId });
    services = servicesSnap
      .filter((service) => !service._init && service.active !== false)
      .filter((service) => service.archived !== true && service.isArchived !== true)
      .map((service) => ({
        id: service.id,
        name: service.name || 'Service',
        duration: parseInt(service.duration, 10) || 60,
        bufferTime: parseInt(service.bufferTime, 10) || 0,
        price: parseFloat(service.price) || 0,
        description: service.description || '',
      }));
  } catch (err) {
    console.warn('[loadCompanySeedContext] Could not load services:', err);
    throw new Error('Could not load your company services. Try again or use default demo data.');
  }

  if (services.length === 0) {
    throw new Error(
      'No active services found. Add services in Settings or switch to default demo data.'
    );
  }

  return {
    staff,
    services,
    usingCompanyData: true,
    companyHours,
  };
}
